import styled from 'styled-components';
import { GraphQLError } from 'graphql';
import { useTranslation } from 'react-i18next';
import { rgba } from 'polished';

interface IProps {
	error?: GraphQLError;
	warning?: boolean;
	showValidationDetails?: boolean;
	overrideFieldNames?: {
		[key: string]: string;
	};
}

interface IValidationErrorDetail {
	context: {
		label: string;
		value: any;
		key: string;
	};
	message: string;
	path: (string | number)[];
	type: string;
}

const Element = styled.p`
	color: ${(props) => props.theme.colors.danger};
	background: ${(props) => rgba(props.theme.colors.danger, 0.1)};
	padding: 8px;
	border-radius: 2px;
	margin: 10px 0px;
`;

const Warning = styled.p`
	color: ${(props) => props.theme.colors.yellow};
	background: ${(props) => rgba(props.theme.colors.yellow, 0.25)};
	padding: 8px;
	border-radius: 2px;
	margin: 10px 0px;
`;

const ValidationErrorsElement = styled.ul`
	padding-left: 1.5em;
	margin-block-end: 0;
`;

function ValidationErrors(props: {
	error?: GraphQLError;
	showValidationDetails?: boolean;
	overrideFieldNames?: {
		[key: string]: string;
	};
}) {
	const { t } = useTranslation();
	const { error, showValidationDetails, overrideFieldNames } = props;

	const { key, data } = error?.extensions?.error ?? ({} as any);

	if (key !== 'errors.validation.validationError' || !showValidationDetails) {
		return null;
	}

	return (
		<ValidationErrorsElement>
			{data.details?.map((detail: IValidationErrorDetail, index: number) => {
				const field = overrideFieldNames?.[detail.context.key] ?? detail.context.key;

				return <li key={index}>{t(`errors.validation.rules.${detail.type}`, { field })}</li>;
			})}
		</ValidationErrorsElement>
	);
}

export default function GraphQLErrorMessage(props: IProps) {
	const { t } = useTranslation();
	const { error, warning } = props;

	if (!error) {
		return null;
	}

	const { key, data } = error?.extensions?.error ?? ({} as any);

	if (warning) {
		return (
			<Warning>
				{t(key, data) as any}
				<ValidationErrors {...props}></ValidationErrors>
			</Warning>
		);
	}

	return (
		<Element>
			{t(key, data) as any}
			<ValidationErrors {...props}></ValidationErrors>
		</Element>
	);
}
